// src/App.js
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import { GoogleMap, Marker, InfoWindow, useLoadScript } from '@react-google-maps/api';
import { Instagram, Facebook, Twitter } from 'lucide-react';

// ✅ 自己参照の削除（import Home from "./App"; は不要）
// 既存ページ
import Login from './components/Login';
import Register from './components/Register';
import StorePage from './components/StorePage';
import LP from './components/LP';
import { ArchiveNotice } from './components/ArchiveNotice';

import './styles.css';

// 追加ページ（存在しない場合は作成済みのファイル名に合わせてください）
import Footer from "./components/Footer";
import LegalTokushoho from "./LegalTokushoho";
import LegalTerms from "./LegalTerms";
import LegalPrivacy from "./LegalPrivacy";

import {
  PAYMENT_LABELS as LABELS,
  PAYMENT_COLORS as COLORS,
  PAYMENT_SECTIONS,
} from './constants/payments';

const activePaymentMethods = (methods) =>
  (Array.isArray(methods) ? methods : []).filter((id) => id !== 'line_pay');

// ルートが変わるたびにスクロールをリセット
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    // ブラウザの自動復元を無効化（戻る/進む時も最上部にしたい場合）
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
    // Safari などで behavior 未対応でも落ちないように try/catch
    try {
      window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    } catch {
      window.scrollTo(0, 0);
    }
  }, [pathname]);

  return null;
}

function PageMeta() {
  const { pathname } = useLocation();
  const ORIGIN = 'https://map.cashlessmap.com';
  const SITE_NAME = 'キャッシュレス決済マップ';
  const DEFAULT_OG = '/ogp.png';

  useEffect(() => {
    // ルートごとのタイトル/説明を定義
    const map = {
      '/': {
        title: 'キャッシュレス決済マップ | 近くのキャッシュレス対応店舗を検索',
        desc: 'キャッシュレス対応店舗を検索・表示。絞り込みや現在地からの探索ができます。PayPay、d払い、楽天Pay、au PAY、メルペイ、nanaco、WAON、iD、QUICPay、Suica、交通系ICカードなど、対応する決済方法で店舗を検索できます。無料で使えるキャッシュレス店舗検索サービス。'
      },
      '/login': {
        title: '店舗用ログイン | キャッシュレス決済マップ',
        desc: '店舗向け管理ページへのログイン。'
      },
      '/register': {
        title: '新規店舗登録 | キャッシュレス決済マップ',
        desc: '店舗オーナー向けの新規登録ページ。あなたの店が対応するキャッシュレス決済を1分で掲載。月300円、解約自由。'
      },
      '/store': {
        title: '店舗管理ページ | キャッシュレス決済マップ',
        desc: '店舗情報の編集や公開、決済管理ができます。'
      },
      '/store-page': {
        title: '店舗管理ページ | キャッシュレス決済マップ',
        desc: '店舗情報の編集や公開、決済管理ができます。'
      },
      '/lp': {
        title: '掲載のご案内 | キャッシュレス決済マップ | 店舗向け情報掲載サービス',
        desc: 'あなたの店が対応するキャッシュレス決済を1分で掲載。月300円、解約自由。PayPay、d払い、楽天Pay、au PAY、メルペイ、nanaco、WAON、iD、QUICPay、Suica、交通系ICカードなど、対応する決済方法を地図上に表示できます。初月無料コード「CASHFIRST」あり。'
      },
      '/legal/tokushoho': {
        title: '特定商取引法に基づく表記 | キャッシュレス決済マップ',
        desc: '事業者情報、支払方法、返品等についての表示。'
      },
      '/legal/terms': {
        title: '利用規約 | キャッシュレス決済マップ',
        desc: '本サービスのご利用条件について。'
      },
      '/legal/privacy': {
        title: 'プライバシーポリシー | キャッシュレス決済マップ',
        desc: '個人情報の取扱いについて。'
      }
    };

    // マッチング（/store など前方一致させたいパスは startsWith で簡易対応）
    const key = Object.keys(map).find(k => {
      if (k === '/') return pathname === '/';
      return pathname.startsWith(k);
    }) || '/';

    const { title, desc } = map[key];
    const url = `${ORIGIN}${pathname}`;

    // <title>
    document.title = title;

    // メタタグ更新ヘルパー
    const upsertMeta = (name, content, attribute = 'name') => {
      if (!content) return;
      let el = document.head.querySelector(`meta[${attribute}="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attribute, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    // <meta name="description">
    upsertMeta('description', desc);

    // canonical
    let canonical = document.head.querySelector("link[rel='canonical']");
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', url);

    // OGP
    upsertMeta('og:site_name', SITE_NAME, 'property');
    upsertMeta('og:type', 'website', 'property');
    upsertMeta('og:url', url, 'property');
    upsertMeta('og:title', title, 'property');
    upsertMeta('og:description', desc, 'property');
    upsertMeta('og:image', `${ORIGIN}${DEFAULT_OG}`, 'property');
    upsertMeta('og:image:width', '1200', 'property');
    upsertMeta('og:image:height', '630', 'property');
    upsertMeta('og:image:alt', `${title} - ${SITE_NAME}`, 'property');
    upsertMeta('og:locale', 'ja_JP', 'property');

    // Twitter Card
    upsertMeta('twitter:card', 'summary_large_image');
    upsertMeta('twitter:title', title);
    upsertMeta('twitter:description', desc);
    upsertMeta('twitter:image', `${ORIGIN}${DEFAULT_OG}`);

    // 構造化データ（ホームページの場合）
    if (pathname === '/') {
      const upsertJsonLd = (id, obj) => {
        let script = document.getElementById(id);
        if (!script) {
          script = document.createElement('script');
          script.type = 'application/ld+json';
          script.id = id;
          document.head.appendChild(script);
        }
        script.textContent = JSON.stringify(obj);
      };

      // LocalBusiness構造化データ（サービス提供者として）
      upsertJsonLd('page-localbusiness', {
        "@context": "https://schema.org",
        "@type": "LocalBusiness",
        "name": "キャッシュレス決済マップ",
        "alternateName": "Cashless Map",
        "url": ORIGIN,
        "logo": `${ORIGIN}/logo512.png`,
        "image": `${ORIGIN}/logo512.png`,
        "description": "キャッシュレス対応店舗を検索できる地図サービス。PayPay、d払い、楽天Pay、au PAY、メルペイ、nanaco、WAON、iD、QUICPay、Suica、交通系ICカードなど、対応する決済方法で店舗を検索できます。",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "銀座1丁目12番4号N&E BLD.6F",
          "addressLocality": "中央区",
          "addressRegion": "東京都",
          "postalCode": "104-0061",
          "addressCountry": "JP"
        },
        "telephone": "+81-3-0000-0000",
        "email": "customer-support-cashless@cashlessmap.com",
        "priceRange": "店舗掲載: 月額300円",
        "currenciesAccepted": "JPY",
        "paymentAccepted": "クレジットカード",
        "areaServed": {
          "@type": "Country",
          "name": "日本"
        },
        "sameAs": []
      });
    }
  }, [pathname]);

  return null;
}

const useGAPageview = (measurementId) => {
   const location = useLocation();
   useEffect(() => {
     if (!window.gtag || !measurementId) return;
       // SPA のページ遷移でも page_path を更新
     window.gtag('config', measurementId, {
       page_path: location.pathname + window.location.search,
     });
  }, [location.pathname, location.search, measurementId]); 
};

function GAListener({ id }) {
  useGAPageview(id);
  return null;
}


const LIBRARIES = ['places'];
const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

const getCurrentPositionSmart = (opt1 = {}, opt2 = {}) =>
  new Promise((resolve, reject) => {
    if (!('geolocation' in navigator)) return reject(new Error('Geolocation not supported'));
    const fast = { enableHighAccuracy: false, timeout: 12000, maximumAge: 300000, ...opt1 };
    const high = { enableHighAccuracy: true, timeout: 20000, maximumAge: 0, ...opt2 };
    navigator.geolocation.getCurrentPosition(
      resolve,
      (err1) => {
        if (err1.code === 1) return reject(err1);
        navigator.geolocation.getCurrentPosition(
          resolve,
          (err2) => {
            if (err2.code === 2 || err2.code === 3) {
              const id = navigator.geolocation.watchPosition(
                (pos) => { navigator.geolocation.clearWatch(id); resolve(pos); },
                (err3) => { navigator.geolocation.clearWatch(id); reject(err3); },
                high
              );
              setTimeout(() => { navigator.geolocation.clearWatch(id); reject(err2); }, 10000);
            } else {
              reject(err2);
            }
          },
          high
        );
      },
      fast
    );
  });

/** 小さめ"フルネーム"決済バッジ（枠＝色、塗りなし） */
const MiniPayBadge = ({ id, onLight = false }) => {
  const label = LABELS[id] || id;
  const color = COLORS[id] || '#555';
  return (
    <span
      title={label}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '2px 8px',
        borderRadius: 14,
        border: `1px solid ${color}`,
        background: onLight ? '#fff' : 'transparent',
        color: onLight ? color : '#fff',
        fontSize: 11,
        lineHeight: 1.2,
        whiteSpace: 'nowrap',
        fontWeight: 600,
      }}
    >
      {label}
    </span>
  );
};

const PayBadge = ({ id, active, onClick }) => {
  const label = LABELS[id] || id;
  const color = COLORS[id] || '#555';
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: '6px 10px',
        borderRadius: 20,
        border: active ? `2px solid ${color}` : `1px solid ${color}`,
        background: active ? color : '#1f1f1f',
        color: '#fff',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        cursor: 'pointer',
        fontSize: 12,
      }}
    >
      <span style={{ fontWeight: 600 }}>{label}</span>
    </button>
  );
};

// 既定POIの情報を出さない
const MAP_OPTIONS = {
  clickableIcons: false,
  styles: [
    { featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] },
    { featureType: 'poi.business', elementType: 'all', stylers: [{ visibility: 'off' }] },
  ],
};

const MIN_MARKER_ZOOM = 12;
const sizeForZoom = (z) => (z >= 17 ? 64 : z >= 15 ? 48 : z >= 13 ? 36 : 28);

const MapPage = ({ isLoaded, loadError, onMobileFiltersStateChange }) => {
  const SHOW_MARKERS = true;

  const [shops, setShops] = useState([]);
  const [selected, setSelected] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');

  const mapRef = useRef(null);
  const [center, setCenter] = useState({ lat: 35.681236, lng: 139.767125 });
  const [zoom, setZoom] = useState(14);

  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [payFilter, setPayFilter] = useState([]);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(() => {
    if (typeof window !== 'undefined' && window.matchMedia) {
      return !window.matchMedia('(max-width: 768px)').matches; // モバイルなら false
    }
    return true;
  });
  
  // スマホ版の検出
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  // スマホ版で決済方法の絞り込みが非表示の状態を親に通知
  useEffect(() => {
    if (onMobileFiltersStateChange) {
      onMobileFiltersStateChange(isMobile && !filtersOpen);
    }
  }, [isMobile, filtersOpen, onMobileFiltersStateChange]);

  // モバイル時：フィルタの開閉に応じてページスクロールを制御
  useEffect(() => {
    // PC は常に解除
    if (!isMobile) {
      document.body.style.overflow = '';
      document.body.style.overscrollBehavior = '';
      return;
    }
    if (filtersOpen) {
      // フィルタ表示中：ページスクロール可（地図は 2 本指パン）
      document.body.style.overflow = 'auto';
      document.body.style.overscrollBehavior = 'auto';
    } else {
      // フィルタ非表示：ページスクロールをロック（地図は 1 本指パン）
      document.body.style.overflow = 'hidden';
      // iOS Safari のバウンス抑止
      document.body.style.overscrollBehavior = 'contain';
    }
    // 画面遷移やアンマウント時の掃除
    return () => {
      document.body.style.overflow = '';
      document.body.style.overscrollBehavior = '';
    };
  }, [isMobile, filtersOpen]);

  // （既存）モバイル時：フィルタの開閉に応じてページスクロールを制御 の useEffect の直後に追加
  const prevOpenRef = useRef(filtersOpen);

  useEffect(() => {
    // 直前が「表示中(true)」→ 今回「非表示(false)」になった瞬間だけ最上部へ
    const wasOpen = prevOpenRef.current;
    if (isMobile && wasOpen && !filtersOpen) {
      // レイアウト反映後にスクロールさせると確実
      requestAnimationFrame(() => {
        try {
          // Safari等で behavior 未対応でも落ちないように
          window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
        } catch {
          window.scrollTo(0, 0);
        }
        // iOS WebKit 向けの保険
        document.body.scrollTop = 0;
        document.documentElement.scrollTop = 0;
      });
    }
    prevOpenRef.current = filtersOpen;
  }, [filtersOpen, isMobile]);



  //  地図ラッパーはフレックスで残り領域をすべて埋める
  const mapWrapperStyle = {
    flex: 1,
    minHeight: isMobile ? 260 : 380, // ここはお好みで（地図が極端に小さくならない下限）
    width: '100%',
    ...(isMobile && !filtersOpen ? {
      height: '100%',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0
    } : {})
  };
  //  GoogleMap 自体はラッパーにぴったり張り付かせる
  const mapInnerStyle = { height: '100%', width: '100%' };



  const FILTER_GROUPS = useMemo(
    () => (Array.isArray(PAYMENT_SECTIONS) ? PAYMENT_SECTIONS : []),
    []
  );

  const allCategories = useMemo(() => {
    const coll = new Intl.Collator('ja');
    return Array.from(
      new Set(
        shops.map((s) => (s.category || '').trim()).filter((v) => v && v.length > 0)
      )
    ).sort(coll.compare);
  }, [shops]);

  const filtered = useMemo(() => {
    let list = shops;
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter((s) => {
        const name = (s.shopName || '').toLowerCase();
        const owner = (s.ownerName || '').toLowerCase();
        const cat = (s.category || '').toLowerCase();
        return name.includes(q) || owner.includes(q) || cat.includes(q);
      });
    }
    if (category && category !== 'ALL') {
      list = list.filter((s) => (s.category || '').trim() === category);
    }
    if (payFilter.length > 0) {
      list = list.filter((s) => {
        const pm = activePaymentMethods(s.paymentMethods);
        return pm.some((id) => payFilter.includes(id)); // OR条件
      });
    }
    return list;
  }, [shops, search, category, payFilter]);

  const suggestions = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return [];
    return shops
      .filter(
        (s) =>
          (s.shopName || '').toLowerCase().includes(q) ||
          (s.category || '').toLowerCase().includes(q)
      )
      .slice(0, 12);
  }, [shops, search]);

  const fetchShops = async () => {
    try {
      const res = await fetch(`${API_BASE}/shops`);
      const text = await res.text();
      let data = null; try { data = JSON.parse(text); } catch {}
      if (!res.ok) throw new Error((data && data.error) || `HTTP ${res.status} - ${text.slice(0, 200)}`);
      setShops(Array.isArray(data) ? data : []);
      setErrorMsg('');
    } catch (e) {
      console.error('店舗データの取得エラー:', e);
      setErrorMsg('店舗データの取得に失敗しました。');
    }
  };

  useEffect(() => { fetchShops(); }, []);


// 現在地へ移動（Permissions API 対応＋Safari フォールバック）
  const goToCurrentLocation = useCallback(async () => {
    const onSuccess = (pos) => {
      const next = { lat: pos.coords.latitude, lng: pos.coords.longitude };
      setCenter(next);
      if (mapRef?.current) {
        mapRef.current.panTo(next);
        const z = typeof mapRef.current.getZoom === 'function'
          ? Math.max(mapRef.current.getZoom(), 16)
          : 16;
        mapRef.current.setZoom?.(z);
      } else {
        setZoom?.((prev) => Math.max(prev ?? 14, 14));
      }
    };

  const onError = (err) => {
    // 1: PERMISSION_DENIED / 2: POSITION_UNAVAILABLE / 3: TIMEOUT
    if (err?.code === 1) {
      alert('位置情報の利用が拒否されています。ブラウザ設定から位置情報を許可してください');
    } else if (err?.code === 2) {
      alert('現在地を取得できませんでした（位置情報が利用できません）。屋外で再試行してください。');
    } else if (err?.code === 3) {
      alert('現在地の取得がタイムアウトしました。通信状況を確認して再試行してください。');
    } else {
      alert('現在地を取得できませんでした。しばらくしてから再試行してください。');
    }
  };

  if (!('geolocation' in navigator)) {
    alert('このブラウザでは現在地取得を利用できません。ブラウザ設定から位置情報を許可してください');
    return;
  }

  const ask = () =>
    navigator.geolocation.getCurrentPosition(onSuccess, onError, {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 0,
    });

  try {
    if ('permissions' in navigator && navigator.permissions.query) {
      const { state } = await navigator.permissions.query({ name: 'geolocation' });
      if (state === 'denied') {
        alert('位置情報の利用が拒否されています。ブラウザ設定から位置情報を許可してください');
        return;
      }
      // granted / prompt → そのまま取得（prompt のときは各ブラウザの許可ダイアログが出る）
      return ask();
    }
  } catch {
    // Safari など Permissions API 非対応はそのままフォールバック
  }

  ask();
}, [setCenter, setZoom, mapRef]);

// 省略: useState/useEffect などの後あたりに追加
const markerIcon = useMemo(() => {
  if (!isLoaded || !window.google) return undefined;
  const sz = sizeForZoom(zoom);
  return {
    url: `${process.env.PUBLIC_URL}/icons/pin.png`,
    scaledSize: new window.google.maps.Size(sz, sz),
    anchor: new window.google.maps.Point(sz / 2, sz), // 先端=下中央
  };
}, [isLoaded, zoom]);


 // 初回表示時：位置情報の許可を確認し、許可済み/プロンプトなら現在地へ移動
useEffect(() => {
  // マップがまだ読み込み中なら待つ
  if (!isLoaded) return;

  // 毎回自動で許可ダイアログを出したくない場合はセッション中1回だけ
  const AUTO_KEY = 'cm.autoLocateAsked';
  if (sessionStorage.getItem(AUTO_KEY)) return;
  sessionStorage.setItem(AUTO_KEY, '1');

  // Geolocation 未対応なら何もしない
  if (!('geolocation' in navigator)) return;

  const run = async () => {
    try {
      // Permissions API が使えるなら現在の状態を確認
      if ('permissions' in navigator && navigator.permissions?.query) {
        const { state } = await navigator.permissions.query({ name: 'geolocation' });
        if (state === 'denied') {
          // 拒否されているときは自動では何もしない（アラートは出さない）
          return;
        }
        // granted / prompt → 既存の関数で現在地へ（prompt の場合はブラウザの許可ダイアログが出る）
        goToCurrentLocation();
      } else {
        // Safari など Permissions API 非対応 → そのまま取得（必要ならブラウザがプロンプト表示）
        goToCurrentLocation();
      }
    } catch {
      // 失敗時は何もしない（ユーザ操作の「現在地」ボタンで再試行可能）
    }
  };

  run();
}, [isLoaded, goToCurrentLocation]);


  const togglePay = (id) => {
    setPayFilter((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  if (loadError) return <div style={{ padding: 16 }}>地図の読み込みに失敗しました。</div>;
  if (!isLoaded) return <div style={{ padding: 16 }}>地図を読み込み中です...</div>;

  return (
    <div className="map-container" style={isMobile && !filtersOpen ? { height: 'calc(100vh - 80px - var(--archive-notice-height, 0px))', overflow: 'hidden', position: 'fixed', top: 'calc(80px + var(--archive-notice-height, 0px))', left: 0, right: 0, bottom: 0 } : {}}>
      {/* コントロールバー */}
      <div className="controls-panel" style={isMobile && !filtersOpen ? { position: 'absolute', top: 0, left: 0, right: 0, zIndex: 10 } : {}}>
        <div style={{ display: 'grid', rowGap: 12 }}>
          <div className="controls-sticky-top">
            {/* 検索 & カテゴリ & 現在地 */}
            <div
              style={{
                display: 'flex',
                gap: 8,
                marginBottom: 8,
                flexWrap: 'wrap',
                alignItems: 'center',
              }}
            >
              <div style={{ position: 'relative', flex: '1 1 420px', minWidth: 300 }}>
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="店舗名・カテゴリで検索"
                  onFocus={() => setSuggestOpen(true)}
                  onBlur={() => setTimeout(() => setSuggestOpen(false), 120)}
                  style={{
                    width: '100%',
                    boxSizing: 'border-box',
                    padding: isMobile ? '6px 8px' : '8px 10px',
                    borderRadius: 8,
                    border: '1px solid #d1d5db',
                    background: 'rgba(255,255,255,0.9)',
                    color: '#374151',
                    fontSize: isMobile ? '14px' : '16px'
                  }}
                />
                {suggestOpen && search.trim() && suggestions.length > 0 && (
                  <div
                    style={{
                      position: 'absolute',
                      top: 'calc(100% + 6px)',
                      left: 0,
                      right: 0,
                      background: 'rgba(255,255,255,0.95)',
                      border: '1px solid #d1d5db',
                      borderRadius: 10,
                      padding: 8,
                      maxHeight: 300,
                      overflowY: 'auto',
                      boxShadow: '0 10px 24px rgba(0,0,0,0.15)',
                      color: '#374151',
                      zIndex: 20,
                    }}
                  >
                    {suggestions.map((s) => (
                      <div
                        key={s._id}
                        style={{
                          padding: '8px 10px',
                          borderRadius: 8,
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 10,
                          cursor: 'pointer'
                        }}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => {
                          setSelected(s);
                          setSearch(s.shopName || '');
                          setSuggestOpen(false);
                          if (s.location && mapRef.current) {
                            const pos = { lat: s.location.lat, lng: s.location.lng };
                            mapRef.current.panTo(pos);
                            mapRef.current.setZoom(16);
                          }
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 600 }}>{s.shopName || '(名称未設定)'}</div>
                          <div style={{ fontSize: 12, opacity: 0.85 }}>{(s.category || 'その他').trim()}</div>
                          <div style={{ marginTop: 6, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            {activePaymentMethods(s.paymentMethods).slice(0, 24).map((id) => (
                              <MiniPayBadge key={id} id={id} onLight={true} />
                            ))}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', gap: 8, flex: '0 0 auto' }}>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  style={{
                    flex: '0 0 240px',
                    minWidth: 240,
                    padding: isMobile ? '6px 8px' : '8px 10px',
                    borderRadius: 8,
                    border: '1px solid #d1d5db',
                    background: 'rgba(255,255,255,0.9)',
                    color: '#374151',
                    fontSize: isMobile ? '14px' : '16px'
                  }}
                >
                  <option value="">{'店舗の種類'}</option>
                  <option value="ALL">すべて</option>
                  {allCategories.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>

                <button
                  type="button"
                  onClick={goToCurrentLocation}
                  style={{
                    flex: '0 0 auto',
                    whiteSpace: 'nowrap',
                    padding: isMobile ? '6px 10px' : '8px 12px',
                    borderRadius: 8,
                    border: '1px solid #d1d5db',
                    background: 'rgba(255,255,255,0.9)',
                    color: '#374151',
                    fontSize: isMobile ? '14px' : '16px'
                  }}
                >
                  現在地
                </button>
              </div>
            </div>
            
            {/* スマホ版で決済方法の絞り込みが非表示の状態では、決済方法の絞り込みを表示ボタンをここに配置 */}
            {isMobile && !filtersOpen && (
              <div style={{ marginTop: 6, display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setFiltersOpen(true)}
                  style={{
                    padding: '6px 10px',
                    borderRadius: 10,
                    border: '1.5px solid #6b7280',
                    background: 'rgba(255,255,255,0.9)',
                    color: '#374151',
                    fontSize: '12px',
                    fontWeight: 700,
                  }}
                >
                  決済方法の絞り込みを表示
                </button>
              </div>
            )}
          </div>

          {filtersOpen ? (
            <div style={{ display: 'grid', gap: 10 }}>
              {FILTER_GROUPS.map((grp) => (
                <div key={grp.key} style={{ display: 'grid', gap: 8 }}>
                  <div style={{ fontWeight: 700, color: '#374151', fontSize: 14 }}>{grp.title}</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {(grp.ids || []).map((id) => {
                      const active = payFilter.includes(id);
                      const label = LABELS[id] || id;         // ← 追加：表示名
                      const color = COLORS[id] || '#3c495f';  // ← 追加：ブランド色（なければデフォルト）
                      return (
                        <button
                          key={id}
                          onClick={() => togglePay(id)}
                          aria-pressed={active}
                          style={{
                            padding: '6px 10px',
                            borderRadius: 20,
                            border: active ? `2px solid ${color}` : `1px solid ${color}`,
                            background: active ? color : '#fff',
                            color: active ? '#fff' : color,
                            fontWeight: 600,
                            fontSize: 12,
                            margin: '6px 6px 0 0',
                            outline: 'none'
                          }}
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}


              {/* 右下に「絞り込みを隠す」ボタン */}
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setFiltersOpen(false)}
                  style={{
                    marginTop: 6,
                    padding: '6px 10px',
                    borderRadius: 10,
                    border: '1.5px solid #6b7280',
                    background: 'rgba(255,255,255,0.9)',
                    color: '#374151',
                    fontSize: 13,
                  }}
                >
                  決済方法の絞り込みを隠す
                </button>
              </div>
            </div>
          ) : (
            // PC版では従来通り検索バー行の下に「表示」ボタンを配置
            !isMobile && (
              <div style={{ marginTop: 6, display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setFiltersOpen(true)}
                  style={{
                    padding: '8px 12px',
                    borderRadius: 10,
                    border: '1.5px solid #6b7280',
                    background: 'rgba(255,255,255,0.9)',
                    color: '#374151',
                    fontSize: '14px',
                    fontWeight: 700,
                  }}
                >
                  決済方法の絞り込みを表示
                </button>
              </div>
            )
          )}

        </div>
      </div>

      {/* 地図 */}
      <div style={mapWrapperStyle}>
        <GoogleMap
          mapContainerClassName="google-map"
          mapContainerStyle={mapInnerStyle}   // ← 高さ/幅 100%
          center={center}
          zoom={zoom}
          onLoad={(m) => { mapRef.current = m; }}
          onZoomChanged={() => setZoom(mapRef.current?.getZoom?.() ?? zoom)}
          options={{
            ...MAP_OPTIONS,
            // モバイル：フィルタ開＝2本指 / 非表示＝1本指（ページスクロール制御は既存の body.overflow でOK）
            gestureHandling: isMobile ? (filtersOpen ? 'cooperative' : 'greedy') : 'auto',
            draggable: true,
            mapTypeControlOptions: {
              position: window.google.maps.ControlPosition.BOTTOM_RIGHT,
            },
            zoomControl: true,
            streetViewControl: false,
            fullscreenControl: false,
          }}
        >
          {zoom >= MIN_MARKER_ZOOM && filtered.map((s) => (
            <Marker
              key={s._id}
              position={{ lat: s.location.lat, lng: s.location.lng }}
              icon={markerIcon || undefined}
              onClick={() => setSelected(s)}
            />
          ))}

          {selected && (
            <InfoWindow
              position={{ lat: selected.location.lat, lng: selected.location.lng }}
              onCloseClick={() => setSelected(null)}
            >
              <div style={{ maxWidth: 260, color: '#000' }}>
                <div style={{ fontWeight: 700, marginBottom: 4, color: '#000' }}>{selected.shopName}</div>
                <div style={{ fontSize: 12, opacity: 0.95, marginBottom: 8, color: '#000' }}>
                  {(selected.category || 'その他').trim()}
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
                  {activePaymentMethods(selected.paymentMethods).map((id) => (
                    <MiniPayBadge key={id} id={id} onLight={true} />
                  ))}
                </div>
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${selected.location.lat},${selected.location.lng}`}
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    display: 'inline-flex',
                    padding: '6px 10px',
                    borderRadius: 8,
                    border: '1px solid #1a73e8',
                    background: '#1a73e8',
                    color: '#fff',
                    fontSize: 12,
                    fontWeight: 600,
                    textDecoration: 'none',
                    marginRight: 6,
                    marginBottom: 6,
                  }}
                >
                  Googleマップで見る
                </a>
                {/* SNS・公式サイトリンク */}
                {selected.officialWebsite && (
                  <a
                    href={selected.officialWebsite}
                    target="_blank"
                    rel="noreferrer"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                      padding: '6px 10px',
                      borderRadius: 8,
                      border: '1px solid #6366f1',
                      background: '#6366f1',
                      color: '#fff',
                      fontSize: 12,
                      fontWeight: 600,
                      textDecoration: 'none',
                      marginRight: 6,
                      marginBottom: 6,
                    }}
                  >
                    公式HP
                  </a>
                )}
                {selected.officialInstagram && (
                  <a
                    href={selected.officialInstagram}
                    target="_blank"
                    rel="noreferrer"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                      padding: '6px 10px',
                      borderRadius: 8,
                      border: '1px solid #E4405F',
                      background: '#E4405F',
                      color: '#fff',
                      fontSize: 12,
                      fontWeight: 600,
                      textDecoration: 'none',
                      marginRight: 6,
                      marginBottom: 6,
                    }}
                    title="Instagram"
                  >
                    <Instagram size={14} />
                  </a>
                )}
                {selected.officialFacebook && (
                  <a
                    href={selected.officialFacebook}
                    target="_blank"
                    rel="noreferrer"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                      padding: '6px 10px',
                      borderRadius: 8,
                      border: '1px solid #1877F2',
                      background: '#1877F2',
                      color: '#fff',
                      fontSize: 12,
                      fontWeight: 600,
                      textDecoration: 'none',
                      marginRight: 6,
                      marginBottom: 6,
                    }}
                    title="Facebook"
                  >
                    <Facebook size={14} />
                  </a>
                )}
                {selected.officialX && (
                  <a
                    href={selected.officialX}
                    target="_blank"
                    rel="noreferrer"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                      padding: '6px 10px',
                      borderRadius: 8,
                      border: '1px solid #000',
                      background: '#000',
                      color: '#fff',
                      fontSize: 12,
                      fontWeight: 600,
                      textDecoration: 'none',
                      marginRight: 6,
                      marginBottom: 6,
                    }}
                    title="X（Twitter）"
                  >
                    <Twitter size={14} />
                  </a>
                )}
              </div>
            </InfoWindow>
          )}
        </GoogleMap>
      </div>

      {errorMsg && <div className="error-banner">{errorMsg}</div>}
    </div>
  );
};

const Header = () => {
  const location = useLocation();
  const onAuth =
    location.pathname.startsWith('/login') ||
    location.pathname.startsWith('/register') ||
    location.pathname.startsWith('/store') ||
    location.pathname.startsWith('/store-page') ||
    location.pathname.startsWith('/legal');

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center flex-shrink-0">
            <Link to="/" className="bg-gradient-to-r from-primary via-purple-600 to-blue-600 bg-clip-text text-transparent hover:opacity-80 transition-opacity text-sm sm:text-xl font-bold">
              キャッシュレス決済マップ
            </Link>
          </div>
          {onAuth ? (
            <Link to="/" className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white px-4 py-2 sm:px-6 sm:py-2 rounded-md text-sm sm:text-base font-medium transition-all transform hover:scale-105">
              マップ
            </Link>
          ) : (
            <nav className="flex items-center gap-2 sm:gap-16">
              <Link to="/lp" className="text-foreground/70 hover:text-foreground transition-colors font-medium text-xs sm:text-lg">
                掲載のご案内
              </Link>
              <Link to="/login" className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white px-4 py-2 sm:px-6 sm:py-2 rounded-md text-xs sm:text-base font-medium transition-all transform hover:scale-105">
                店舗用ログイン
              </Link>
            </nav>
          )}
        </div>
      </div>
    </header>
  );
};

const App = () => {
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: LIBRARIES,
  });

  // スマホ版で決済方法の絞り込みが非表示の状態を管理
  const [hideFooter, setHideFooter] = useState(false);
  
  const handleMobileFiltersStateChange = (isMobileFiltersHidden) => {
    setHideFooter(isMobileFiltersHidden);
  };

  useEffect(() => {
    const setOffsets = () => {
      const h = document.querySelector('.header');
      const f = document.querySelector('.site-footer, .footer'); // Footer 側のクラスいずれか
      if (h) document.documentElement.style.setProperty('--header-offset', `${h.offsetHeight}px`);
      if (f) document.documentElement.style.setProperty('--footer-offset', `${f.offsetHeight}px`);
    };
    setOffsets();
    window.addEventListener('resize', setOffsets);
    return () => window.removeEventListener('resize', setOffsets);
  }, []);

  return (
    <Router>
      <ScrollToTop />
      <PageMeta />
      <GAListener id={process.env.REACT_APP_GA_MEASUREMENT_ID} />
      <div className="app-root bg-white">
        <Routes>
          {/* ランディングページは独自のヘッダー・フッターを使用 */}
          <Route path="/lp" element={<LP />} />
          {/* その他のページは共通のヘッダー・フッターを使用 */}
          <Route path="/*" element={
            <>
              <Header />
              <main className="page-content" style={{ paddingTop: '80px' }}>
                <ArchiveNotice />
                <Routes>
                  {/* ホーム＝地図 */}
                  <Route path="/" element={<MapPage isLoaded={isLoaded} loadError={loadError} onMobileFiltersStateChange={handleMobileFiltersStateChange} />} />
                  {/* 認証/管理 */}
                  <Route path="/login" element={<Login />} />
                  <Route path="/register" element={<Register isLoaded={isLoaded} />} />
                  {/* 互換のため /store と /store-page の両方を用意 */}
                  <Route path="/store" element={<StorePage isLoaded={isLoaded} loadError={loadError} />} />
                  <Route path="/store-page" element={<StorePage isLoaded={isLoaded} loadError={loadError} />} />
                  {/* リーガル */}
                  <Route path="/legal/tokushoho" element={<LegalTokushoho />} />
                  <Route path="/legal/terms" element={<LegalTerms />} />
                  <Route path="/legal/privacy" element={<LegalPrivacy />} />
                </Routes>
              </main>
              {!hideFooter && <Footer />}
            </>
          } />
        </Routes>
      </div>
    </Router>
  );
};

export default App;
